import { throttle } from 'lodash-es';

const THROTTLE_MS = 100;

/**
 * Subset of the ResizeObserver API exposed by createSafeResizeObserver.
 * Using an explicit Pick rather than `as ResizeObserver` so callers get a
 * compile error if they try to access properties not present on the wrapper
 * (e.g. the prototype chain) instead of a silent runtime surprise.
 */
export type SafeResizeObserver = Pick<ResizeObserver, 'observe' | 'unobserve' | 'disconnect'>;

type CreateSafeResizeObserverOptions = Readonly<{
  /** Throttle interval in ms (default: 100). */
  throttleMs?: number;
  /**
   * When true, invoke the callback directly instead of deferring it through
   * requestAnimationFrame. Use this when the rAF deferral causes a visible
   * layout jump on initial render. Default: false.
   */
  skipRAF?: boolean;
}>;

/**
 * Creates a ResizeObserver that prevents "ResizeObserver loop" errors by
 * throttling callbacks and deferring state updates to requestAnimationFrame.
 *
 * Drop-in replacement for `new ResizeObserver(callback)`:
 *
 * @example
 * const observer = createSafeResizeObserver((entries) => {
 *   doSomething(entries[0]);
 * });
 * observer.observe(element);
 * // cleanup:
 * observer.disconnect();
 *
 * Uses lodash throttle with default options (leading: true, trailing: true),
 * so the callback fires immediately on the first resize event (no delay on
 * initial render) and at most once per 100ms thereafter. The trailing call
 * guarantees the last event is never dropped.
 *
 * By default the callback is deferred through requestAnimationFrame to
 * prevent "ResizeObserver loop" browser errors. Pass `{ skipRAF: true }`
 * to invoke the callback synchronously when the rAF deferral causes a
 * visible layout jump.
 *
 * @param callback - The ResizeObserver callback to invoke safely
 * @param options  - Optional configuration (throttleMs, skipRAF)
 */
export function createSafeResizeObserver(callback: ResizeObserverCallback, options?: CreateSafeResizeObserverOptions): SafeResizeObserver {
  const { throttleMs = THROTTLE_MS, skipRAF = false } = options ?? {};

  let frameId: number | null = null;

  const throttledCallback = throttle((entries: ResizeObserverEntry[], obs: ResizeObserver) => {
    if (skipRAF) {
      callback(entries, obs);
      return;
    }

    if (frameId !== null) {
      cancelAnimationFrame(frameId);
    }

    frameId = requestAnimationFrame(() => {
      callback(entries, obs);
      frameId = null;
    });
  }, throttleMs);

  // eslint-disable-next-line compat/compat
  const observer = new ResizeObserver(throttledCallback);

  return {
    observe: observer.observe.bind(observer),
    unobserve: observer.unobserve.bind(observer),
    disconnect() {
      throttledCallback.cancel();

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }

      observer.disconnect();
    },
  };
}
